import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button, Chip, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip, TextField,
  MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress,
  InputAdornment,
} from '@mui/material';
import { Add, Refresh, AccessTime, Check, Schedule } from '@mui/icons-material';
import { projectService, TimeEntry, Project, ProjectTask } from '../../services/projectService';
import { formatCurrency } from '../../utils/currency';

const TimeTrackingPage: React.FC = () => {
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [selectedProject, setSelectedProject] = useState<number | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [totalHours, setTotalHours] = useState('0');
  const [billableHours, setBillableHours] = useState('0');
  const [formData, setFormData] = useState({
    projectId: '', taskId: '', entryDate: '', hours: '', description: '', billable: true,
  });
  const [formLoading, setFormLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const projectsData = await projectService.getProjects();
      setProjects(projectsData);

      const timeData = await projectService.getAllTimeEntries({
        projectId: selectedProject ? selectedProject : undefined,
      });
      setEntries(timeData.entries);
      setTotalHours(timeData.totalHours);
      setBillableHours(timeData.billableHours);
    } catch (err: any) {
      setError(err.message || 'Failed to load time entries');
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchProjectTasks = async (projectId: number) => {
    try {
      const tasksData = await projectService.getProjectTasks(projectId);
      setTasks(tasksData);
    } catch (e) {
      setTasks([]);
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      projectId: selectedProject ? selectedProject.toString() : '',
      taskId: '', entryDate: new Date().toISOString().split('T')[0],
      hours: '', description: '', billable: true,
    });
    if (selectedProject) {
      fetchProjectTasks(selectedProject);
    }
    setDialogOpen(true);
  };

  const handleProjectChange = (projectId: string) => {
    setFormData({ ...formData, projectId, taskId: '' });
    if (projectId) {
      fetchProjectTasks(parseInt(projectId));
    } else {
      setTasks([]);
    }
  };

  const handleSave = async () => {
    if (!formData.projectId || !formData.hours || !formData.entryDate) {
      setError('Project, date, and hours are required');
      return;
    }
    setFormLoading(true);
    try {
      await projectService.createTimeEntry(parseInt(formData.projectId), {
        taskId: formData.taskId ? parseInt(formData.taskId) : undefined,
        entryDate: formData.entryDate,
        hours: parseFloat(formData.hours),
        description: formData.description || undefined,
        billable: formData.billable,
      });
      setDialogOpen(false);
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to save time entry');
    } finally {
      setFormLoading(false);
    }
  };

  const handleApprove = async (entryId: number) => {
    try {
      await projectService.updateTimeEntry(entryId, { approved: true });
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to approve entry');
    }
  };

  const pendingApproval = entries.filter(e => !e.approved).length;
  const approvedCount = entries.filter(e => e.approved).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>Time Tracking</Typography>
          <Typography variant="body2" color="text.secondary">Log and manage project time entries</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh"><IconButton onClick={fetchData}><Refresh /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenDialog}>Log Time</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
            <AccessTime color="primary" sx={{ fontSize: 32, mb: 1 }} />
            <Typography variant="h5" fontWeight="bold">{totalHours}h</Typography>
            <Typography variant="caption" color="text.secondary">Total Hours</Typography>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h5" fontWeight="bold" color="success.main">{billableHours}h</Typography>
            <Typography variant="caption" color="text.secondary">Billable Hours</Typography>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Schedule color="warning" sx={{ fontSize: 32, mb: 1 }} />
            <Typography variant="h5" fontWeight="bold">{pendingApproval}</Typography>
            <Typography variant="caption" color="text.secondary">Pending Approval</Typography>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Check color="success" sx={{ fontSize: 32, mb: 1 }} />
            <Typography variant="h5" fontWeight="bold">{approvedCount}</Typography>
            <Typography variant="caption" color="text.secondary">Approved</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField select fullWidth size="small" label="Filter by Project" value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value === '' ? '' : parseInt(e.target.value as string))}>
              <MenuItem value="">All Projects</MenuItem>
              {projects.map(p => <MenuItem key={p.id} value={p.id}>{p.project_name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" color="text.secondary">{entries.length} entries</Typography>
          </Grid>
        </Grid>
      </Paper>

      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box> : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.50' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Project</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Task</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Hours</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No time entries found</Typography>
                  </TableCell></TableRow>
                ) : entries.map(entry => (
                  <TableRow key={entry.id} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                    <TableCell>{new Date(entry.entry_date).toLocaleDateString()}</TableCell>
                    <TableCell>{entry.project_name || '-'}</TableCell>
                    <TableCell>{entry.task_title || '-'}</TableCell>
                    <TableCell>{entry.user_name || '-'}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">{entry.hours}h</Typography>
                      {entry.billable && <Chip label="Billable" size="small" color="success" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell>{entry.description || '-'}</TableCell>
                    <TableCell>
                      <Chip label={entry.approved ? 'Approved' : 'Pending'} size="small"
                        color={entry.approved ? 'success' : 'warning'} />
                    </TableCell>
                    <TableCell>
                      {!entry.approved && (
                        <Tooltip title="Approve">
                          <IconButton size="small" color="success" onClick={() => handleApprove(entry.id)}>
                            <Check fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Log Time</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField select fullWidth label="Project" value={formData.projectId}
                onChange={(e) => handleProjectChange(e.target.value)} required>
                <MenuItem value="">Select Project</MenuItem>
                {projects.map(p => <MenuItem key={p.id} value={p.id.toString()}>{p.project_name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField select fullWidth label="Task (Optional)" value={formData.taskId}
                onChange={(e) => setFormData({ ...formData, taskId: e.target.value })}
                disabled={!formData.projectId}>
                <MenuItem value="">No specific task</MenuItem>
                {tasks.map(t => <MenuItem key={t.id} value={t.id.toString()}>{t.title}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField fullWidth label="Date" type="date" value={formData.entryDate}
                onChange={(e) => setFormData({ ...formData, entryDate: e.target.value })}
                InputLabelProps={{ shrink: true }} required />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField fullWidth label="Hours" type="number" value={formData.hours}
                onChange={(e) => setFormData({ ...formData, hours: e.target.value })} required
                inputProps={{ min: 0.25, max: 24, step: 0.25 }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Description" value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })} multiline rows={2} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField select fullWidth label="Billable" value={formData.billable ? 'yes' : 'no'}
                onChange={(e) => setFormData({ ...formData, billable: e.target.value === 'yes' })}>
                <MenuItem value="yes">Yes - Billable</MenuItem>
                <MenuItem value="no">No - Non-billable</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={formLoading}>{formLoading ? 'Saving...' : 'Log Time'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimeTrackingPage;
